import type { QuoteStatus, RequestStatus, UrgencyLevel } from '../../types';
import { getQuoteStatusColor, getRequestStatusColor } from '../../types/status';

const badgeColorMap: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-800 border-gray-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  green: 'bg-green-100 text-green-800 border-green-200',
};

const DEFAULT_BADGE_CLASSES = badgeColorMap.gray;

export function getBadgeColorClasses(color: string): string {
  return badgeColorMap[color] ?? DEFAULT_BADGE_CLASSES;
}

export function getRequestStatusBadgeClasses(status: RequestStatus): string {
  return getBadgeColorClasses(getRequestStatusColor(status));
}

const quoteBadgeColorOverrides: Partial<Record<QuoteStatus, keyof typeof badgeColorMap>> = {
  approved: 'green',
  changes_requested: 'amber',
  pending_assignment: 'blue',
  exclusive_pending: 'purple',
  assigned: 'green',
  archived: 'gray',
};

export function getQuoteStatusBadgeClasses(status: QuoteStatus): string {
  const color = quoteBadgeColorOverrides[status] ?? getQuoteStatusColor(status);
  return getBadgeColorClasses(color);
}

const urgencyBadgeMap: Record<UrgencyLevel, string> = {
  low: badgeColorMap.green,
  normal: badgeColorMap.blue,
  high: badgeColorMap.orange,
  emergency: badgeColorMap.red,
};

export function getUrgencyBadgeClasses(urgency: UrgencyLevel): string {
  return urgencyBadgeMap[urgency] ?? DEFAULT_BADGE_CLASSES;
}

/**
 * Formats a numeric amount as a US Dollar currency string.
 *
 * @param amount - The numeric value to format as currency.
 * @returns The formatted currency string in USD (e.g., "$1,234.56").
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}